import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  AdminGlobalEmailItemResponse,
  AdminGlobalEmailListResponse,
} from "@/lib/api/generated";
import { EmailDetailDialog } from "@/components/admin/email-detail-dialog";
import { getAdminAllEmailsServer } from "@/server/admin";

interface SearchParams {
  page?: number;
}

const PAGE_SIZE = 20;

export const Route = createFileRoute("/admin/outbox")({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
  }),
  loaderDeps: ({ search }) => ({
    page: search.page || 1,
  }),
  loader: async ({ deps }) => {
    const skip = ((deps.page || 1) - 1) * PAGE_SIZE;

    const response = await getAdminAllEmailsServer({
      data: {
        skip,
        limit: PAGE_SIZE,
        direction: "outbound",
      },
    });

    return {
      emails: response as AdminGlobalEmailListResponse,
    };
  },
  component: AdminOutboxPage,
});

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function getRecipientPreview(email: AdminGlobalEmailItemResponse): string {
  if (email.recipient_emails.length === 0) return "-";
  if (email.recipient_emails.length === 1) return email.recipient_emails[0] ?? "-";
  return `${email.recipient_emails[0]} +${email.recipient_emails.length - 1}`;
}

function AdminOutboxPage() {
  const { t } = useTranslation("admin");
  const { emails } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const currentPage = search.page || 1;
  const [selectedEmail, setSelectedEmail] = useState<AdminGlobalEmailItemResponse | null>(null);
  const [isEmailDetailOpen, setIsEmailDetailOpen] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(emails.total / PAGE_SIZE));
  }, [emails.total]);

  const handlePageChange = (newPage: number) => {
    const safePage = Math.min(Math.max(1, newPage), totalPages);
    navigate({
      search: {
        page: safePage,
      },
    });
  };

  const handleEmailClick = (email: AdminGlobalEmailItemResponse) => {
    setSelectedEmail(email);
    setIsEmailDetailOpen(true);
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
        <div className="space-y-0.5">
          <div className="text-sm font-medium">{t("outbox.title")}</div>
          <div className="text-xs text-muted-foreground">
            {t("outbox.total", { total: emails.total })} •{" "}
            {t("outbox.pageInfo", { current: currentPage, total: totalPages })}
          </div>
        </div>
        <Badge variant="secondary" className="shrink-0">
          {emails.items.length}
        </Badge>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {emails.items.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t("outbox.empty")}
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                <TableHead className="w-44">{t("outbox.workspace")}</TableHead>
                <TableHead className="w-10"></TableHead>
                <TableHead>{t("outbox.subject")}</TableHead>
                <TableHead className="w-48">{t("outbox.to")}</TableHead>
                <TableHead className="w-36">{t("outbox.date")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emails.items.map((email) => (
                <TableRow
                  key={email.message_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleEmailClick(email)}
                >
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Building2 className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium">{email.workspace_name}</div>
                        <div className="truncate text-[10px] text-muted-foreground">
                          {email.workspace_slug}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ArrowUpRight className="h-3.5 w-3.5 text-blue-500" />
                  </TableCell>
                  <TableCell>
                    <div className="min-w-0">
                      <div className="truncate text-xs font-medium">
                        {email.subject || t("outbox.noSubject")}
                      </div>
                      <div className="max-w-xs truncate text-[10px] text-muted-foreground">
                        {email.body_preview}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-0">
                      <div className="truncate text-xs">{getRecipientPreview(email)}</div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {email.recipient_emails.join(", ") || "-"}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(email.sent_at ?? email.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between border-t px-5 py-3">
        <span className="text-xs text-muted-foreground">
          {t("outbox.showing", { count: emails.items.length, total: emails.total })}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={currentPage <= 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            {t("outbox.previous")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={currentPage >= totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            {t("outbox.next")}
          </Button>
        </div>
      </div>

      {selectedEmail && (
        <EmailDetailDialog
          open={isEmailDetailOpen}
          onOpenChange={(open) => {
            setIsEmailDetailOpen(open);
            if (!open) setSelectedEmail(null);
          }}
          email={selectedEmail}
          t={t}
          i18nPrefix="workspaces.emails.detail"
        />
      )}
    </div>
  );
}
